import axios from 'axios'

const baseURL =
  process.env.NODE_ENV === 'development'
    ? process.env.REACT_APP_API_URL_DEV
    : process.env.REACT_APP_API_URL

export interface BillingForItau {
  id: string
  installment_number: number
  payment_part_name: string
  value: string
  calculated_total: string | null
  paid_amount: string | null
  due_date: string
  status: string
  itau_nosso_numero: string | null
  itau_codigo_barras: string | null
  itau_linha_digitavel: string | null
  itau_pix_emv: string | null
  itau_pix_txid: string | null
  itau_boleto_status: string | null
  itau_boleto_emitido_em: string | null
  itau_due_date: string | null
  itau_reissue_count: number
  original_due_date: string
  has_itau_boleto: boolean
  can_generate_boleto: boolean
  payment_date: string | null
  uau_payment_registered_at: string | null
  itau_penalties_waived: boolean
}

export interface GenerateBoletosResult {
  success: Array<{ billingId: string; nossoNumero: string }>
  errors: Array<{ billingId: string; error: string }>
}

function buildHeaders(token: string, userContextEncoded?: string) {
  const headers: Record<string, string> = {
    Authorization: `bearer ${token}`
  }
  if (userContextEncoded) {
    headers['X-User-Context'] = userContextEncoded
  }
  return headers
}

export async function getContractBillingsForItau(
  contractId: string,
  token: string,
  userContextEncoded?: string
): Promise<BillingForItau[]> {
  const response = await axios.get(
    `${baseURL}/billings/contract/${contractId}/billings-for-itau`,
    { headers: buildHeaders(token, userContextEncoded) }
  )
  return response.data
}

export async function generateItauBoletos(
  billingIds: string[],
  token: string,
  userContextEncoded?: string,
  dueDateOverride?: string,
  perBillingDates?: { [billingId: string]: string },
  perBillingWaivePenalties?: { [billingId: string]: boolean }
): Promise<GenerateBoletosResult> {
  const response = await axios.post(
    `${baseURL}/billings/generate-itau-boletos`,
    {
      billing_ids: billingIds,
      due_date_override: dueDateOverride,
      per_billing_dates: perBillingDates,
      per_billing_waive_penalties: perBillingWaivePenalties
    },
    { headers: buildHeaders(token, userContextEncoded) }
  )
  return response.data
}

export async function reissueItauBolecode(
  billingId: string,
  newDueDate: string,
  token: string,
  userContextEncoded?: string
): Promise<{ success: boolean }> {
  const response = await axios.post(
    `${baseURL}/billings/reissue-itau-bolecode`,
    { billing_id: billingId, new_due_date: newDueDate },
    { headers: buildHeaders(token, userContextEncoded) }
  )
  return response.data
}

export async function previewItauValue(
  billingId: string,
  date: string | undefined,
  token: string,
  userContextEncoded?: string
): Promise<{
  valorReajustado: number
  valorCorrecao: number
  valorPrincipal: number
  valorCorrecaoAtraso: number
  valorMulta: number
  valorJurosAtraso: number
  valorSemPenalidades: number
  date: string | null
} | null> {
  const response = await axios.post(
    `${baseURL}/billings/preview-itau-value`,
    { billing_id: billingId, date },
    { headers: buildHeaders(token, userContextEncoded) }
  )
  return response.data
}

export async function cancelItauBolecode(
  billingId: string,
  token: string,
  userContextEncoded?: string
): Promise<{
  billingId: string
  oldNossoNumero: string
  itauCancelled: boolean
  itauCancelError?: string
}> {
  const response = await axios.post(
    `${baseURL}/billings/${billingId}/cancel-itau-bolecode`,
    {},
    { headers: buildHeaders(token, userContextEncoded) }
  )
  return response.data
}

export async function syncUauPayment(
  billingId: string,
  token: string,
  userContextEncoded?: string
): Promise<{
  status: 'already_registered' | 'registered' | 'skipped_already_synced'
  numReceb?: number
  message?: string
  valorRegistered?: number
  valorAcrescimo?: number
  valorDesconto?: number
  lossy?: boolean
}> {
  const response = await axios.post(
    `${baseURL}/billings/${billingId}/sync-uau-payment`,
    {},
    { headers: buildHeaders(token, userContextEncoded) }
  )
  return response.data
}

export async function resendBolecodeEmail(
  billingId: string,
  token: string,
  userContextEncoded?: string
): Promise<{ sent: boolean }> {
  const response = await axios.post(
    `${baseURL}/billings/resend-bolecode-email`,
    { billing_id: billingId },
    { headers: buildHeaders(token, userContextEncoded) }
  )
  return response.data
}

export async function downloadBoletoPDF(
  billingId: string,
  token: string,
  userContextEncoded?: string
): Promise<void> {
  const response = await axios.get(
    `${baseURL}/billings/${billingId}/boleto-pdf`,
    {
      headers: buildHeaders(token, userContextEncoded),
      responseType: 'blob'
    }
  )
  const blob = new Blob([response.data], { type: 'application/pdf' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `boleto-${billingId}.pdf`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}
